import { LexicalCollaboration } from '@lexical/react/LexicalCollaborationContext';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import type { FCWithChildren } from '@pbk-web/types';
import type { SlotDefinitions } from '@pbk-web/ui';
import { withSlot } from '@pbk-web/ui';
import { useMemo, useRef } from 'react';

import { EditorChromeProvider } from './core/context/EditorChromeContext';
import { EditorPropsProvider } from './core/context/EditorPropsContext';
import { EnabledFeaturesProvider } from './core/context/FeaturesContext';
import { EditorDisabledPlugin } from './core/EditorDisabledPlugin';
import { EditorShell } from './core/EditorShell';
import { FeatureProviders } from './core/FeatureProviders';
import { FocusEventsPlugin } from './core/FocusEventsPlugin';
import { lexicalTheme } from './core/theme';
import { ValuePlugin } from './core/ValuePlugin';
import { LIST_NUMBERING_HTML } from './features/list-numbering/listNumbering';
import { EDITOR_FEATURES, EDITOR_NODES } from './features/registry';
import type { EditorSlot, LexicalProps } from './types';

/**
 * The Lexical-based rich/plain text input; see `README.md` for how it fits together.
 * - Features are switched on by `mode` and `features`, as decided in `features/registry.ts`.
 * - The composer config (nodes, collaboration, initial editable state) is frozen at mount: give the
 *   component a new `key` to change `mode` or toggle collaboration.
 * - Content goes in `PbkLexicalInput.Slot`s (`leading`, `trailing`, `before`, `after`; see `EditorSlot`).
 */
const PbkLexicalInputComponent: FCWithChildren<LexicalProps & { _slots: EditorSlot }> = (props) => {
  // Read once: LexicalComposer freezes its config, so toggling this needs a remount
  const collaborating = useRef(!!props.features?.collaboration?.enabled).current;

  const initialConfig = useRef<InitialConfigType>({
    namespace: props.namespace,
    onError: console.error,
    editable: !props.disabled,
    theme: lexicalTheme,
    nodes: EDITOR_NODES,
    // Like `nodes`, on every editor: it only touches lists, which plain mode never has
    html: LIST_NUMBERING_HTML,
    // The Yjs document is the source of truth while collaborating. `null` stops Lexical seeding a
    // default state, which every connecting client would otherwise write into the shared document.
    ...(collaborating && { editorState: null }),
  });

  const features = useMemo(() => EDITOR_FEATURES.filter((feature) => feature.isEnabled(props)), [props]);
  const enabledNames = useMemo(() => new Set(features.map(({ name }) => name)), [features]);

  return (
    <LexicalCollaboration>
      <LexicalComposer initialConfig={initialConfig.current}>
        <EditorPropsProvider value={props}>
          <EnabledFeaturesProvider value={enabledNames}>
            <EditorChromeProvider>
              <FeatureProviders features={features}>
                <EditorShell />
                <ValuePlugin />
                <FocusEventsPlugin />
                <EditorDisabledPlugin />
                {features.map(({ name, Plugin }) => Plugin && <Plugin key={name} />)}
              </FeatureProviders>
            </EditorChromeProvider>
          </EnabledFeaturesProvider>
        </EditorPropsProvider>
      </LexicalComposer>
    </LexicalCollaboration>
  );
};

export const PbkLexicalInput = withSlot<LexicalProps, SlotDefinitions<EditorSlot[]>>(PbkLexicalInputComponent);
